import { Injectable, signal, computed } from '@angular/core';
import { supabase } from '../supabase/supabase.client';

export interface PlatformSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  setting_type: 'string' | 'number' | 'boolean' | 'json';
  description?: string;
  category: string;
  created_at: string;
  updated_at: string;
}

interface SupabaseError {
  message: string;
  code?: string;
  details?: string;
  hint?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private settingsCache = signal<Map<string, any>>(new Map());
  private loadingSettings = signal(false);
  private lastLoadTime = signal<number>(0);
  private readonly CACHE_DURATION = 300000; // 5 minutes in milliseconds

  // Computed signal for cache validity
  private isCacheValid = computed(() => {
    const age = Date.now() - this.lastLoadTime();
    return age < this.CACHE_DURATION && this.settingsCache().size > 0;
  });

  // Optimized select fields - only fetch what's needed for cache
  private readonly SETTINGS_SELECT_FIELDS = 'setting_key, setting_value, setting_type' as const;
  private readonly SETTINGS_FULL_FIELDS = '*' as const;

  constructor() {
    this.loadSettings();
  }

  /**
   * Load all settings from database and cache them
   */
  async loadSettings(forceReload: boolean = false): Promise<void> {
    // Use computed signal to check cache validity
    if (!forceReload && this.isCacheValid()) return;
    if (this.loadingSettings()) return;
    
    try {
      this.loadingSettings.set(true);
      
      const { data, error } = await supabase
        .from('platform_settings')
        .select(this.SETTINGS_SELECT_FIELDS);

      if (error) {
        this.logError('Failed to load settings', error);
        throw new Error(`Failed to load settings: ${error.message}`);
      }

      // Use update() instead of set() for better signal performance
      this.settingsCache.update(() => {
        const cache = new Map<string, any>();
        data?.forEach((setting: any) => {
          cache.set(setting.setting_key, this.parseSettingValue(setting));
        });
        return cache;
      });

      this.lastLoadTime.set(Date.now());
    } catch (error) {
      this.logError('Exception loading settings', error as Error);
      throw error;
    } finally {
      this.loadingSettings.set(false);
    }
  }

  /**
   * Parse setting value based on type - optimized with early returns
   */
  private parseSettingValue(setting: { setting_value: string; setting_type: string }): any {
    const { setting_value, setting_type } = setting;

    if (setting_type === 'number') {
      const parsed = parseFloat(setting_value);
      return isNaN(parsed) ? setting_value : parsed;
    }

    if (setting_type === 'boolean') {
      return setting_value === 'true';
    }

    if (setting_type === 'json') {
      try {
        return JSON.parse(setting_value);
      } catch (error) {
        console.warn(`Failed to parse JSON for setting, returning raw value:`, error);
        return setting_value;
      }
    }

    return setting_value;
  }

  /**
   * Get a setting value by key
   */
  getSetting<T = any>(key: string, defaultValue?: T): T {
    const value = this.settingsCache().get(key);
    return value !== undefined ? value : (defaultValue as T);
  }

  /**
   * Get all settings - with full field selection
   */
  async getAllSettings(): Promise<PlatformSetting[]> {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select(this.SETTINGS_FULL_FIELDS)
        .order('category', { ascending: true })
        .order('setting_key', { ascending: true });

      if (error) {
        this.logError('Failed to get all settings', error);
        throw new Error(`Failed to get all settings: ${error.message}`);
      }

      return (data as PlatformSetting[]) || [];
    } catch (error) {
      this.logError('Exception getting all settings', error as Error);
      throw error;
    }
  }

  /**
   * Get settings by category
   */
  async getSettingsByCategory(category: string): Promise<PlatformSetting[]> {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select(this.SETTINGS_FULL_FIELDS)
        .eq('category', category)
        .order('setting_key', { ascending: true });

      if (error) {
        this.logError('Failed to get settings by category', error, { category });
        throw new Error(`Failed to get settings by category: ${error.message}`);
      }

      return (data as PlatformSetting[]) || [];
    } catch (error) {
      this.logError('Exception getting settings by category', error as Error, { category });
      throw error;
    }
  }

  /**
   * Update a setting - with optimistic cache update
   */
  async updateSetting(key: string, value: any): Promise<void> {
    const stringValue = this.convertToString(value);
    
    // Optimistic update - update cache immediately
    this.settingsCache.update(cache => {
      cache.set(key, value);
      return cache;
    });

    try {
      const { error } = await supabase
        .from('platform_settings')
        .update({ setting_value: stringValue })
        .eq('setting_key', key);

      if (error) {
        // Rollback cache on error
        await this.loadSettings(true);
        this.logError('Failed to update setting', error, { key });
        throw new Error(`Failed to update setting: ${error.message}`);
      }
    } catch (error) {
      this.logError('Exception updating setting', error as Error, { key });
      throw error;
    }
  }

  /**
   * Update multiple settings at once - using upsert for batch performance
   */
  async updateMultipleSettings(settings: { key: string; value: any }[]): Promise<void> {
    if (settings.length === 0) return;

    // Optimistic cache update
    this.settingsCache.update(cache => {
      settings.forEach(({ key, value }) => {
        cache.set(key, value);
      });
      return cache;
    });

    try {
      // Use upsert with array for batch update - single network call
      const updates = settings.map(({ key, value }) => ({
        setting_key: key,
        setting_value: this.convertToString(value)
      }));

      const { error } = await supabase
        .from('platform_settings')
        .upsert(updates, { 
          onConflict: 'setting_key',
          ignoreDuplicates: false 
        });

      if (error) {
        // Rollback cache on error
        await this.loadSettings(true);
        this.logError('Failed to update multiple settings', error);
        throw new Error(`Failed to update multiple settings: ${error.message}`);
      }
    } catch (error) {
      this.logError('Exception updating multiple settings', error as Error);
      throw error;
    }
  }

  /**
   * Create a new setting
   */
  async createSetting(setting: Omit<PlatformSetting, 'id' | 'created_at' | 'updated_at'>): Promise<void> {
    try {
      const { error } = await supabase
        .from('platform_settings')
        .insert([setting]);

      if (error) {
        this.logError('Failed to create setting', error, { key: setting.setting_key });
        throw new Error(`Failed to create setting: ${error.message}`);
      }

      // Update cache with new setting
      this.settingsCache.update(cache => {
        cache.set(setting.setting_key, this.parseSettingValue({
          setting_value: setting.setting_value,
          setting_type: setting.setting_type
        }));
        return cache;
      });
    } catch (error) {
      this.logError('Exception creating setting', error as Error);
      throw error;
    }
  }

  /**
   * Delete a setting
   */
  async deleteSetting(key: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('platform_settings')
        .delete()
        .eq('setting_key', key);

      if (error) {
        this.logError('Failed to delete setting', error, { key });
        throw new Error(`Failed to delete setting: ${error.message}`);
      }

      // Remove from cache
      this.settingsCache.update(cache => {
        cache.delete(key);
        return cache;
      });
    } catch (error) {
      this.logError('Exception deleting setting', error as Error, { key });
      throw error;
    }
  }

  /**
   * Helper to convert any value to string for storage
   */
  private convertToString(value: any): string {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return String(value);
  }

  /**
   * Centralized error logging with structured context
   */
  private logError(
    message: string, 
    error: SupabaseError | Error | any, 
    context?: Record<string, any>
  ): void {
    const errorDetails: Record<string, any> = {
      message,
      timestamp: new Date().toISOString(),
      service: 'SettingsService',
      ...context
    };

    if (error && typeof error === 'object') {
      if ('code' in error) errorDetails['code'] = error['code'];
      if ('details' in error) errorDetails['details'] = error['details'];
      if ('hint' in error) errorDetails['hint'] = error['hint'];
      if ('message' in error) errorDetails['errorMessage'] = error['message'];
    }

    console.error('[SettingsService Error]', errorDetails, error);
  }

  /**
   * Clear cache manually if needed
   */
  clearCache(): void {
    this.settingsCache.set(new Map());
    this.lastLoadTime.set(0);
  }
}
